import React, { useCallback, useEffect } from 'react';
import { useQuery } from 'react-query';
import { Sparkles, User, FileText, Scan, Calendar, Activity, AlertCircle, Pill, TrendingUp, Scale } from 'lucide-react';
import { documentApi } from '../../services/api';
import type { DicomStudy } from '../../types';
import { fromReviewCitation } from '../../types/citation';
import type { ReviewCitation } from '../../types/citation';

import { CopyButton } from '../CopyButton';
import { formatCaseSummary } from '../../utils/copyFormatters';
import { DerivedOutputBanner } from './DerivedOutputBanner';
import { EditableNarrativeSection } from './EditableNarrativeSection';
import { useKeyFindings } from '../../contexts/KeyFindingsContext';
import { useHighlightParam } from '../../hooks/useHighlightParam';

type Citation = ReviewCitation;

interface ClaimantBackgroundSection {
  sectionId: string;
  title: string;
  narrative: string;
  citations?: Citation[];
}

interface PhaseMetrics {
  phase: string;
  durationSeconds: number;
  inputTokens: number;
  outputTokens: number;
  estimatedCostUsd: number;
  parallel: boolean;
  chunkCount?: number;
  subPhases?: any[];
  modelHint?: string;
}

interface MetadataExtractionResponse {
  patients: any[];
  keyDates: any[];
  documentTypes: any[];
  pertinentFacts?: Record<string, any[]>;
  medicalTerms?: any[];
  caseSummary?: string;
  caseSummaryCitations?: Citation[];
  claimantBackground?: ClaimantBackgroundSection[];
  confidence: number;
  reasoning: string;
  durationSeconds?: number;
  estimatedCostUsd?: number;
  totalInputTokens?: number;
  totalOutputTokens?: number;
  phaseBreakdown?: PhaseMetrics[];
  aiVisionCostUsd?: number;
  documentScanCostUsd?: number;
  ocrPageCount?: number;
}

const OVERVIEW_SECTION_IDS = ['index_event', 'treatment', 'current_status', 'prognosis', 'expert_opinion'];

const OVERVIEW_SECTION_ICONS: Record<string, React.FC<{ className?: string }>> = {
  index_event: AlertCircle,
  treatment: Pill,
  current_status: Activity,
  prognosis: TrendingUp,
  expert_opinion: Scale,
};

const OVERVIEW_SECTION_GRADIENTS: Record<string, string> = {
  index_event: 'from-red-50 to-orange-50 dark:from-red-900/20 dark:to-orange-900/20',
  treatment: 'from-purple-50 to-violet-50 dark:from-purple-900/20 dark:to-violet-900/20',
  current_status: 'from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/20',
  prognosis: 'from-teal-50 to-cyan-50 dark:from-teal-900/20 dark:to-cyan-900/20',
  expert_opinion: 'from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20',
};

const OVERVIEW_SECTION_BORDERS: Record<string, string> = {
  index_event: 'border-red-200 dark:border-red-800',
  treatment: 'border-purple-200 dark:border-purple-800',
  current_status: 'border-amber-200 dark:border-amber-800',
  prognosis: 'border-teal-200 dark:border-teal-800',
  expert_opinion: 'border-purple-200 dark:border-purple-800',
};

const OVERVIEW_SECTION_ICON_COLORS: Record<string, string> = {
  index_event: 'text-red-600 dark:text-red-400',
  treatment: 'text-purple-600 dark:text-purple-400',
  current_status: 'text-amber-600 dark:text-amber-400',
  prognosis: 'text-teal-600 dark:text-teal-400',
  expert_opinion: 'text-purple-600 dark:text-purple-400',
};

interface CaseOverviewSubtabProps {
  caseId: string;
}

export const CaseOverviewSubtab: React.FC<CaseOverviewSubtabProps> = ({ caseId }) => {
  // Read metadata from React Query cache
  const { data: metadata } = useQuery<MetadataExtractionResponse | null>(
    ['metadata', caseId],
    { enabled: false } // Don't fetch — just read from cache (useMetadataExtraction hook in CaseDetailPage manages fetching)
  );

  const { data: documentsData } = useQuery(
    ['documents', caseId],
    () => documentApi.getCaseDocuments(caseId),
    { refetchOnMount: 'always', refetchOnWindowFocus: true }
  );

  // DICOM studies for this case
  const { data: dicomStudies } = useQuery<DicomStudy[]>(
    ['dicom-studies', caseId],
    () => documentApi.getDicomStudies(caseId),
    { staleTime: 30000 }
  );

  const documents = (documentsData || []).filter((doc: any) => doc.documentType !== 'instruction_letter');

  const buildCaseSummaryUpdate = useCallback((newNarrative: string) => {
    if (!metadata) return metadata;
    return { ...metadata, caseSummary: newNarrative };
  }, [metadata]);

  const { isEventMarked } = useKeyFindings();

  // Auto-scroll to a highlighted section from Key Findings jump
  const { highlightId, scrollToHighlighted } = useHighlightParam();
  useEffect(() => {
    if (!highlightId || !highlightId.startsWith('section-')) return;
    scrollToHighlighted();
  }, [highlightId, scrollToHighlighted]);

  const buildSectionUpdate = useCallback((sectionId: string, newNarrative: string) => {
    if (!metadata) return metadata;
    return {
      ...metadata,
      claimantBackground: (metadata.claimantBackground || []).map(s =>
        s.sectionId === sectionId ? { ...s, narrative: newNarrative } : s
      ),
    };
  }, [metadata]);

  return (
    <div className="p-6 h-full overflow-auto space-y-4">
      <DerivedOutputBanner
        caseId={caseId}
        analysisLabel="case overview"
        evaluatePrompt="I have corrected some chronology events since the case overview was generated. Review the edited events and assess whether the corrections affect the case summary, key dates, patient details, or any other overview findings."
      />

      {/* Case Summary */}
      {metadata?.caseSummary && (
        <div data-event-id="section-caseSummary" className={`bg-gradient-to-r from-cyan-50 to-indigo-50 dark:from-cyan-900/20 dark:to-indigo-900/20 border rounded-lg p-6 mb-6 transition-colors duration-150 ${
          isEventMarked('section-caseSummary')
            ? 'border-amber-300 dark:border-amber-600 shadow-sm'
            : 'border-cyan-200 dark:border-cyan-800'
        }`}>
          <EditableNarrativeSection
            caseId={caseId}
            sectionKey="caseSummary"
            narrative={metadata.caseSummary}
            citations={metadata.caseSummaryCitations?.map((c: Citation) => {
              const doc = documents.find((d: any) => d.documentId === c.documentId);
              return fromReviewCitation(c, doc?.filename);
            }) || []}
            buildUpdatedMetadata={buildCaseSummaryUpdate}
            icon={<Sparkles className="w-5 h-5 text-cyan-600" />}
            title={<>Case Overview <CopyButton getText={() => formatCaseSummary(metadata.caseSummary)} /></>}
            sourceLabel="Case Overview"
          />
        </div>
      )}

      {/* Clinical Overview Sections (index event, treatment, current status, prognosis) */}
      {(() => {
        const overviewSections = (metadata?.claimantBackground || []).filter(s => OVERVIEW_SECTION_IDS.includes(s.sectionId));
        if (overviewSections.length === 0) return null;
        return overviewSections.map((section, idx) => {
          const Icon = OVERVIEW_SECTION_ICONS[section.sectionId] || FileText;
          const gradient = OVERVIEW_SECTION_GRADIENTS[section.sectionId] || '';
          const defaultBorder = OVERVIEW_SECTION_BORDERS[section.sectionId] || '';
          const iconColor = OVERVIEW_SECTION_ICON_COLORS[section.sectionId] || 'text-gray-600 dark:text-gray-400';
          const sectionMarked = isEventMarked(`section-${section.sectionId}`);
          return (
            <div
              key={`${section.sectionId}-${idx}`}
              data-event-id={`section-${section.sectionId}`}
              className={`bg-gradient-to-r ${gradient} border rounded-lg p-6 mb-6 transition-colors duration-150 ${
                sectionMarked ? 'border-amber-300 dark:border-amber-600 shadow-sm' : defaultBorder
              }`}
            >
              <EditableNarrativeSection
                caseId={caseId}
                sectionKey={section.sectionId}
                narrative={section.narrative}
                citations={section.citations?.map(c => {
                  const doc = documents.find((d: any) => d.documentId === c.documentId);
                  return fromReviewCitation(c, doc?.filename);
                }) || []}
                buildUpdatedMetadata={(newNarrative) => buildSectionUpdate(section.sectionId, newNarrative)}
                icon={<Icon className={`w-5 h-5 ${iconColor}`} />}
                title={<>
                  {section.title}
                  {section.sectionId === 'expert_opinion' && (
                    <span className="ml-2 text-xs font-normal text-purple-600 dark:text-purple-400">
                      Medico-legal opinion — not contemporaneous clinical evidence
                    </span>
                  )}
                </>}
                sourceLabel={`Overview — ${section.title}`}
              />
            </div>
          );
        });
      })()}

      {/* DICOM Studies Overview — shown when DICOM studies exist */}
      {dicomStudies && dicomStudies.length > 0 && (
        <div className="bg-white dark:bg-medical-800 border border-gray-200 dark:border-medical-700 rounded-lg mb-6">
          <div className="p-4 border-b border-gray-200 dark:border-medical-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-medical-100 flex items-center gap-2">
              <Scan className="w-5 h-5 text-cyan-600" />
              DICOM Imaging Studies ({dicomStudies.length})
            </h3>
          </div>
          <div className="divide-y divide-gray-200 dark:divide-medical-700">
            {dicomStudies.map((study) => (
              <div key={study.id} className="p-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-cyan-50 dark:bg-cyan-900/20 rounded-lg flex-shrink-0">
                    <Scan className="w-5 h-5 text-cyan-600 dark:text-cyan-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-gray-900 dark:text-medical-100">
                        {study.studyDescription || study.modality || 'DICOM Study'}
                      </span>
                      <span className="px-2 py-0.5 bg-cyan-50 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400 rounded text-xs font-medium">
                        {study.modality}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 mt-2 text-sm text-gray-600 dark:text-medical-400 flex-wrap">
                      {study.patientName && (
                        <span className="flex items-center gap-1">
                          <User className="w-3.5 h-3.5" />
                          {study.patientName}
                        </span>
                      )}
                      {study.studyDate && (
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5" />
                          {new Date(study.studyDate).toLocaleDateString('en-GB')}
                        </span>
                      )}
                      {study.institutionName && (
                        <span className="text-gray-500 dark:text-medical-500">{study.institutionName}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
